/**
 * The authenticated, tenant-scoped session every app shares.
 *
 * Apps reach this through {@link useSession}, not through route props. That is not
 * a style preference: vue-router evaluates a `props` function during route
 * resolution, once per navigation, while the access token is replaced every five
 * minutes by a timer that involves no navigation at all. Props would hand an app a
 * token that silently goes stale.
 */
import {
    computed,
    inject,
    onScopeDispose,
    ref,
    watch,
    type App,
    type ComputedRef,
    type InjectionKey,
    type Ref,
} from 'vue';
import type { TenantOption } from './tenants';

/**
 * The slice of the Auth0 client a session needs.
 *
 * Narrow on purpose — it is what lets the tests drive this with a plain object
 * instead of a real Auth0 application.
 */
export interface SessionAuthClient {
    isAuthenticated: Ref<boolean>;
    isLoading: Ref<boolean>;
    user: Ref<Record<string, unknown> | undefined>;
    error: Ref<unknown>;
    /**
     * Returns `string | undefined` because that is genuinely what auth0-vue's
     * non-detailed overload promises. Callers here coerce to '' — an empty token
     * reads as "not signed in" everywhere downstream, which is the truthful
     * interpretation of a silent-auth miss.
     */
    getAccessTokenSilently(options?: {
        cacheMode?: 'on' | 'off' | 'cache-only';
        authorizationParams?: Record<string, string>;
    }): Promise<string | undefined>;
    loginWithRedirect(options?: {
        appState?: { target?: string };
        authorizationParams?: Record<string, string>;
    }): Promise<void>;
    logout(options?: { logoutParams?: { returnTo?: string } }): Promise<void>;
}

export interface Session {
    /** Access token for the default audience. Empty string until sign-in completes. */
    readonly authToken: Ref<string>;
    /** Confirmed tenant id, or null. */
    readonly tenantId: Ref<string | null>;
    readonly tenantLabel: ComputedRef<string>;
    readonly isAuthenticated: Ref<boolean>;
    readonly isLoading: Ref<boolean>;
    readonly user: Ref<Record<string, unknown> | undefined>;
    readonly error: Ref<unknown>;
    /** True once both a token and a confirmed tenant are held. */
    readonly isReady: ComputedRef<boolean>;
    /** Escape hatch for an app that needs a different audience. */
    getToken(options?: { audience?: string; tenantId?: string }): Promise<string>;
    /**
     * Picker action. Signs in when unauthenticated, otherwise re-mints a token
     * carrying the new tenant's claim. `target` is the route to land on afterwards.
     */
    selectTenant(tenantId: string, target?: string): Promise<void>;
    signOut(): Promise<void>;
    dispose(): void;
}

export const SESSION_KEY: InjectionKey<Session> = Symbol('uat-apps:session');

const REFRESH_MS = 5 * 60 * 1000;
export const TENANT_STORAGE_KEY = 'uat-apps:tenant-id';

/** The tenant confirmed this session, read straight from storage. For the route guard. */
export function readStoredTenantId(): string | null {
    try {
        return sessionStorage.getItem(TENANT_STORAGE_KEY);
    } catch {
        return null;
    }
}

export function createSession(
    auth: SessionAuthClient,
    options: { tenants: TenantOption[]; returnTo: string },
): Session {
    const authToken = ref('');
    // sessionStorage, not localStorage: a fresh tab re-prompts, so a stale Auth0
    // session can never carry yesterday's tenant into this run.
    const tenantId = ref<string | null>(readStoredTenantId());

    let timer: ReturnType<typeof setInterval> | undefined;

    // Guards against a slow token request finishing after a newer one and writing
    // a stale value. That is reachable: the refresh fired on sign-in is still in
    // flight when a user picks a tenant, and its token carries the *previous*
    // tenant's claim. Last request to start is the one allowed to write.
    let tokenGeneration = 0;

    function stopTimer() {
        if (timer !== undefined) {
            clearInterval(timer);
            timer = undefined;
        }
    }

    async function getToken(o: { audience?: string; tenantId?: string } = {}): Promise<string> {
        const id = o.tenantId ?? tenantId.value;
        const token = await auth.getAccessTokenSilently({
            authorizationParams: {
                ...(o.audience ? { audience: o.audience } : {}),
                ...(id ? { tenant_id: id } : {}),
            },
        });
        return token ?? '';
    }

    async function refresh() {
        const generation = ++tokenGeneration;
        try {
            const token = await getToken();
            if (generation === tokenGeneration) authToken.value = token;
        } catch (e) {
            console.error('[shell] token refresh failed:', e);
        }
    }

    const stopWatch = watch(
        auth.isAuthenticated,
        (authenticated) => {
            // Unconditional, and it must stay that way. The version this replaced
            // only ever assigned a new interval, so every extra false -> true
            // transition left another five-minute timer running for the life of the
            // tab. A long-lived shell that several apps are opened from makes that
            // strictly worse than it was in the single-app original.
            stopTimer();

            if (!authenticated) {
                authToken.value = '';
                return;
            }

            void refresh();
            timer = setInterval(refresh, REFRESH_MS);
        },
        { immediate: true },
    );

    async function selectTenant(id: string, target = '/'): Promise<void> {
        sessionStorage.setItem(TENANT_STORAGE_KEY, id);

        if (!auth.isAuthenticated.value) {
            // `appState.target` is what @auth0/auth0-vue pushes onto the router
            // after handling the callback. It is the only thing carrying the
            // intended route across the redirect — the URL cannot, because the
            // plugin rewrites it to '/'.
            await auth.loginWithRedirect({
                appState: { target },
                authorizationParams: { tenant_id: id },
            });
            return;
        }

        // cacheMode 'off' forces a fresh token: the query service reads tenant from
        // the token's claim and ignores any tenantId sent by a non-admin caller, so
        // a cached token would keep acting as the previous tenant.
        const generation = ++tokenGeneration;
        const token = await auth.getAccessTokenSilently({
            cacheMode: 'off',
            authorizationParams: { tenant_id: id },
        });
        if (generation === tokenGeneration) authToken.value = token ?? '';
        tenantId.value = id;
    }

    async function signOut(): Promise<void> {
        stopTimer();
        authToken.value = '';
        tenantId.value = null;
        sessionStorage.removeItem(TENANT_STORAGE_KEY);
        await auth.logout({ logoutParams: { returnTo: options.returnTo } });
    }

    return {
        authToken,
        tenantId,
        tenantLabel: computed(
            () =>
                options.tenants.find((t) => t.tenantId === tenantId.value)?.name ??
                tenantId.value ??
                '(no tenant)',
        ),
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        user: auth.user,
        error: auth.error,
        isReady: computed(() => Boolean(authToken.value && tenantId.value)),
        getToken,
        selectTenant,
        signOut,
        dispose() {
            stopWatch();
            stopTimer();
        },
    };
}

export function provideSession(app: App, session: Session): void {
    app.provide(SESSION_KEY, session);
}

export function useSession(): Session {
    const session = inject(SESSION_KEY);
    if (!session) {
        throw new Error('useSession() called outside the shell. Provide SESSION_KEY in tests.');
    }
    return session;
}

export interface SessionReadyContext {
    token: string;
    tenantId: string;
}

/**
 * Runs `fn` once the session holds both a token and a tenant, and again whenever
 * the tenant changes.
 *
 * Deliberately does **not** re-fire on a plain token refresh. The token string
 * changes every five minutes, and re-running an app's discovery on each one is
 * exactly what the original's `!discovery.value` check existed to prevent.
 */
export function onSessionReady(fn: (context: SessionReadyContext) => void): void {
    const { authToken, tenantId } = useSession();
    const hasToken = computed(() => Boolean(authToken.value));

    const stop = watch(
        [hasToken, tenantId],
        ([ready, id]) => {
            if (ready && id) fn({ token: authToken.value, tenantId: id });
        },
        { immediate: true },
    );

    onScopeDispose(stop);
}
